import type React from 'react';
import { Icon } from '../../components/ui/Icon';
import { StatusDot } from '../../components/ui/StatusDot';
import { ICONO_ESTADO_SENSOR } from '../../components/domain/iconosDominio';
import type { Sensor } from '../../../domain/entities/Sensor';
import { formatearAntiguedad } from '../../utils/formato';
import type { ContextoVisible } from './useMonitoreo';
import estilos from './Monitoreo.module.css';

interface TarjetaSensorProps {
  sensor: Sensor;
  /** Conexión del nodo al que pertenece; `null` si no puede saberse. */
  nodoEnLinea?: boolean | null;
  /**
   * Qué parte de la ubicación escribir. Lo que ya dice el encabezado del grupo
   * o el filtro activo se omite.
   */
  contexto?: ContextoVisible;
  /** Acaba de cambiar de valor: se resalta un momento. */
  reciente?: boolean;
  onAbrir?: (sensor: Sensor) => void;
}

/**
 * Tarjeta de un sensor con su última lectura.
 *
 * El estado se transmite por icono, color y texto a la vez: quien no distinga
 * los colores sigue sabiendo si algo va mal.
 */
export function TarjetaSensor({
  sensor,
  nodoEnLinea,
  contexto = { cliente: true, area: true, nodo: true },
  reciente = false,
  onAbrir,
}: TarjetaSensorProps) {
  const estado = sensor.estadoCon(nodoEnLinea);
  const desconectado = nodoEnLinea === false;

  /*
   * Dos líneas: dónde está y desde cuándo.
   *
   * Iban juntas y no cabían —el nombre del nodo se comía la antigüedad—, así
   * que la ubicación tiene su propia línea, de lo general a lo concreto, y el
   * tiempo queda debajo, que es lo que se mira de un vistazo.
   */
  const ubicacion = [
    contexto.cliente ? sensor.clienteNombre : null,
    contexto.area ? sensor.areaNombre : null,
    contexto.nodo ? sensor.nodoNombre : null,
  ]
    .filter(Boolean)
    .join(' · ');

  /*
   * Que el nodo no dé latido no se escribe: ya lo dicen el icono ámbar y el
   * color de la tarjeta. La línea se reserva para lo que no se puede deducir
   * mirando, que es cuándo llegó la última lectura.
   */
  const antiguedad = sensor.lectura?.timestamp
    ? formatearAntiguedad(sensor.lectura.timestamp)
    : 'Nunca ha reportado';

  return (
    <article
      className={[
        estilos.tarjeta,
        estado.valor === 'alarma' ? estilos.tarjetaCritica : '',
        desconectado ? estilos.tarjetaDesconectada : '',
        reciente ? estilos.tarjetaReciente : '',
        onAbrir ? estilos.tarjetaPulsable : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={[
        sensor.nombre,
        [sensor.clienteNombre, sensor.areaNombre, sensor.nodoNombre].filter(Boolean).join(' · '),
        `${estado.etiqueta} · ${antiguedad}`,
        onAbrir ? 'Pulsa para ver el detalle' : null,
      ]
        .filter(Boolean)
        .join('\n')}
      /*
       * Pulsable de verdad, no un `div` con `onClick`: con `role` y `tabIndex`
       * se llega con el tabulador y responde a Intro y a la barra, que es como
       * navega quien no usa ratón.
       */
      {...(onAbrir
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: () => onAbrir(sensor),
            onKeyDown: (evento: React.KeyboardEvent) => {
              if (evento.key === 'Enter' || evento.key === ' ') {
                evento.preventDefault();
                onAbrir(sensor);
              }
            },
          }
        : {})}
    >
      <span
        className={estilos.icono}
        style={{ background: `color-mix(in srgb, ${estado.color} 15%, transparent)` }}
      >
        <Icon nombre={ICONO_ESTADO_SENSOR[estado.valor]} tamano={20} color={estado.color} />
      </span>

      <div className={estilos.datos}>
        <div className={estilos.nombre}>{sensor.nombre}</div>
        {ubicacion && <div className={estilos.meta}>{ubicacion}</div>}
        <div className={estilos.metaTiempo}>{antiguedad}</div>
      </div>

      <div className={estilos.valor}>
        {sensor.esDigital && sensor.tieneLectura ? (
          <StatusDot
            color={estado.color}
            encendido={sensor.lectura?.esEncendido}
            texto={sensor.valorNumericoMostrable}
            tamano={9}
            etiqueta={`${sensor.nombre}: ${sensor.valorNumericoMostrable}`}
          />
        ) : (
          <span className={estilos.valorNumero}>
            {sensor.valorNumericoMostrable}
            {sensor.unidadMostrable && (
              <span className={estilos.valorUnidad}>{sensor.unidadMostrable}</span>
            )}
          </span>
        )}
      </div>
    </article>
  );
}
